import dayjs from 'dayjs'

const CURRENT_LIFECYCLE_STATES = new Set(['preliminary', 'finalized'])

export function currentEffectiveJointEvents(events) {
  const currentById = new Map()
  for (const event of events || []) {
    if (!event) continue
    const key = event.id == null ? Symbol() : String(event.id)
    const previous = currentById.get(key)
    if (!previous || Number(event.revision || 0) >= Number(previous.revision || 0)) currentById.set(key, event)
  }
  return [...currentById.values()].filter(event =>
    (!event.lifecycle_state || CURRENT_LIFECYCLE_STATES.has(event.lifecycle_state)) &&
    event.recognition_review_status !== 'excluded')
}

export function replaceJointEventSnapshot(_previous, incoming) {
  return currentEffectiveJointEvents(incoming)
}

export function jointResultState({ events, loading, error }) {
  if (error) return 'error'
  if (loading) return 'loading'
  return currentEffectiveJointEvents(events).length ? 'ready' : 'empty'
}

export function jointEventLifecycleText(event) {
  return ({ finalized: '本轮已结束', withdrawn: '已撤回', superseded: '已被新版本替代' })[event?.lifecycle_state] || '实时更新中'
}

export function latestJointRunState(runs) {
  const latest = Array.isArray(runs) ? runs[0] : null
  if (!latest) return { state: 'unknown', text: '' }
  if (latest.status === 'error') return { state: 'error', text: latest.error || '最新联合计算失败' }
  if (latest.final) return { state: 'finalized', text: '最新联合计算轮次已结束' }
  return { state: 'updating', text: '联合计算实时更新中' }
}

// Only joint records determine station intervals and displayed probability.
export function jointIntervals(events, extent, sid = null) {
  return currentEffectiveJointEvents(events).flatMap(event => {
    const stations = (event.participating_stations || []).map(value => String(value).trim())
    if (sid != null && !stations.includes(String(sid).trim())) return []
    const start = Math.max(dayjs(event.event_start).valueOf(), extent?.min ?? -Infinity)
    const end = Math.min(dayjs(event.event_end).valueOf(), extent?.max ?? Infinity)
    const value = Number(event.mean_prob)
    return Number.isFinite(start) && Number.isFinite(end) && start < end && Number.isFinite(value) && value >= 0 && value <= 1
      ? [{ start, end, value, stations }] : []
  })
}

export function projectJointEvents(events, sid = null) {
  return currentEffectiveJointEvents(events).flatMap(event => (event.participating_stations || [])
    .map(value => String(value).trim())
    .filter(station => sid == null || station === String(sid).trim())
    .map(station => ({ ...event, id: `joint-${event.id}-${station}`, association_id: event.id,
      station_sid: station, instrument_sn: station, max_prob: event.mean_prob,
      duration_seconds: (dayjs(event.event_end).valueOf() - dayjs(event.event_start).valueOf()) / 1000,
      source: 'joint_projection' })))
}

// Waveform series already contains null separators. Never fill those gaps with zero.
export function waveformCoverage(series) {
  const runs = []
  let run = null
  for (const [time, value] of series || []) {
    if (value == null || !Number.isFinite(value)) { run = null; continue }
    if (!run) { run = { start: time, end: time }; runs.push(run) }
    else run.end = time
  }
  return runs
}

export function jointProbabilitySeries(events, extent, coverage, sid = null) {
  if (!extent) return []
  const intervals = jointIntervals(events, extent, sid)
  const runs = (coverage || []).map(run => ({ start: Math.max(run.start, extent.min), end: Math.min(run.end, extent.max) }))
    .filter(run => run.start <= run.end).sort((a, b) => a.start - b.start)
    .reduce((out, run) => {
      const last = out[out.length - 1]
      if (last && run.start <= last.end) last.end = Math.max(last.end, run.end)
      else out.push({ ...run })
      return out
    }, [])
  const points = [[extent.min, null]]
  for (const run of runs) {
    const times = new Set([run.start, run.end])
    intervals.forEach(interval => {
      if (interval.start >= run.start && interval.start <= run.end) times.add(interval.start)
      if (interval.end >= run.start && interval.end <= run.end) times.add(interval.end)
    })
    let previous = null
    for (const time of [...times].sort((a, b) => a - b)) {
      const value = time === extent.max ? 0 : intervals.reduce((max, interval) =>
        time >= interval.start && time < interval.end ? Math.max(max, interval.value) : max, 0)
      if (previous != null && value !== previous) points.push([time, previous])
      points.push([time, value])
      previous = value
    }
    points.push([run.end, null])
  }
  points.push([extent.max, null])
  return points
}
